import React from 'react';
import { CarrierHelper } from '~domain/helper/carrier-helper';
import { PolicySummaryFacade } from '~domain/model/facade/policy/policy-summary-facade';
import { PolicyTag } from '~frontend-common/components/policy-card/policy-tag';
import { Img } from '~frontend-common/components/ui';
import { useTranslator } from '~frontend-common/i18n/translator';
import { CommonImages } from '~frontend-common/images';
import { Box } from '~ui-kit/components/box/box';
import { Button } from '~ui-kit/components/button/button';
import { Divider } from '~ui-kit/components/divider';
import { Typography } from '~ui-kit/components/typography/typography';
import { styled } from '~ui-kit/styled-components';
import { formatProductName } from './format-product-name';
import { PolicyField } from './policy-field';

export interface PolicyCardProps {
  policy: PolicySummaryFacade;
  simple?: boolean;
  renewal?: boolean;
  onModifyPolicy?: (policy: PolicySummaryFacade) => void;
  onDocumentsClicked?: (policy: PolicySummaryFacade, event: React.KeyboardEvent | React.MouseEvent) => void;
}

export function PolicyCard({ policy, simple = false, renewal, onModifyPolicy, onDocumentsClicked }: PolicyCardProps) {
  const translator = useTranslator();
  const { t } = translator;

  const ContainerElement = simple ? SimpleContainer : Container;
  const productName = (
    <Typography $fontFamily="Poppins" $fontSize="18px">
      {formatProductName(translator, policy.productType, policy.homeLine)}
    </Typography>
  );

  return (
    <ContainerElement>
      <Box justifyContent="space-between" alignItems="center">
        <Img src={CommonImages.policies.productTypeIcon[policy.productType]} height="48px" width="48px" />

        {simple && (
          <Box flexDirection="column" flex="1 1 auto" ml={2}>
            {productName}
          </Box>
        )}

        <PolicyTag policy={policy} isRenewal={renewal} />
      </Box>

      {!simple && productName}

      <Divider $my={2} />

      <PolicyField label={t('policies.policyCard.policyNumber')}>{policy.policyNumber || '-'}</PolicyField>

      <PolicyField label={t('policies.policyCard.effectiveDate')}>
        {policy.effectiveDate?.formatToShortDate() ?? '-'}
      </PolicyField>

      <PolicyField label={t('policies.policyCard.expiryDate')}>
        {policy.expirationDate?.formatToShortDate() ?? '-'}
      </PolicyField>

      <PolicyField label={t('policies.policyCard.insurer')}>
        {policy.carrier ? CarrierHelper.getDisplayName(policy.carrier, translator) : '-'}
      </PolicyField>

      {!simple && (
        <ActionsContainer>
          {!policy.isExpired && onModifyPolicy && (
            <ActionButton
              data-id="btn-modify-policy"
              id={`btn-modify-policy-${policy.id}`}
              onClick={() => onModifyPolicy(policy)}
            >
              {t('policies.policyCard.buttonModifyPolicy')}
            </ActionButton>
          )}

          {onDocumentsClicked && (
            <ActionButton
              data-id="btn-documents"
              id={`btn-documents-${policy.id}`}
              onClick={(e) => onDocumentsClicked(policy, e)}
            >
              {t('policies.policyCard.buttonDocuments')}
            </ActionButton>
          )}
        </ActionsContainer>
      )}
    </ContainerElement>
  );
}

const Container = styled(Box).attrs({
  flexDirection: 'column',
  gridGap: 2,
  p: 3,
})`
  box-shadow: 0px 4px 10px 0px #0000001a;
  border-radius: 4px 0px 0px 0px;
`;

const SimpleContainer = styled(Box).attrs({ flexDirection: 'column', gridGap: 2 })``;

const ActionsContainer = styled(Box).attrs({
  flexDirection: 'row',
  flex: '1 1 auto',
  gridGap: 2,
  mt: 2,
})``;

const ActionButton = styled(Button).attrs({
  appearance: 'outlined',
  color: 'secondary',
  size: 'sm',
})`
  flex-shrink: 0;

  svg {
    margin-right: 5px;
  }
`;
